import { UploadOutlined } from "@ant-design/icons"
import { Button, Divider, Flex, Form, Select, Skeleton, Space, Spin, Tag, Typography, Upload, message } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React, { useEffect, useState } from "react"

import { useAppDispatch } from "../../../../../hooks/use-app-dispatch.ts"
import { validateFile } from "../../../../../utils/file-validation.ts"
import {
  getAppendixFiles,
  uploadAppendixFile
} from "../../../redux/thunks/document-templates.thunks.ts"
import type { TemplateDocumentFormData } from "../../../types/template-document-form-data.tsx"
import type { DocumentTemplateFile } from "../../../types/types.ts"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

const MAX_SIZE_MB = 200
const ALLOWED_FILE_EXTENSIONS = ["pdf", "doc", "docx"]

interface AppendixDocumentBlockProps {
  fieldKey: string
  form: FormInstance<TemplateDocumentFormData>
  disabled?: boolean
}

export function AppendixDocumentBlock({
  fieldKey,
  form,
  disabled
}: AppendixDocumentBlockProps) {
  const dispatch = useAppDispatch()
  const [files, setFiles] = useState<DocumentTemplateFile[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)

  const path = ["block_data", fieldKey, "file_id"]
  const hasInitialValue = form.getFieldValue(path)
  const selectedFileId = Form.useWatch(path, form)

  const loadFiles = async () => {
    setLoading(true)
    try {
      const result = await dispatch(getAppendixFiles()).unwrap()
      setFiles(result || [])
      setHasLoaded(true)
    } catch (error) {
      message.error("Failed to load files")
    } finally {
      setLoading(false)
    }
  }

  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && !hasLoaded) {
      void loadFiles()
    }
  }

  const handleUpload = async (file: File) => {
    try {
      setUploading(true)
      const result = await dispatch(uploadAppendixFile(file)).unwrap()

      if (result?.id) {
        setFiles((prev) => [...prev, result])

        form.setFieldsValue({
          block_data: {
            [fieldKey]: {
              file_id: result.id
            }
          }
        })
      }
    } catch (error) {
      message.error("Failed to upload image")
    } finally {
      setUploading(false)
    }
  }

  const handleBeforeUpload = (file: File) => {
    const result = validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS,
      defaultResult: true
    })

    if (result === true) {
      void handleUpload(file)
    }

    return false
  }

  const selectedFile = files.find((file) => file.id === selectedFileId)

  useEffect(() => {
    if (hasInitialValue) void loadFiles()
  }, [])

  return (
    <>
      <Form.Item
        name={["block_data", fieldKey, "title"]}
        label="Appendix Title"
      >
        <TextInput placeholder="e.g. Appendix A - Technical Specifications" />
      </Form.Item>

      {/*<Form.Item*/}
      {/*  name={["block_data", fieldKey, "description"]}*/}
      {/*  label="Description"*/}
      {/*>*/}
      {/*  <TextArea*/}
      {/*    placeholder="Brief description of the appendix content"*/}
      {/*    rows={3}*/}
      {/*  />*/}
      {/*</Form.Item>*/}

      <Form.Item label="Document" className={"mb-0"}>
        <Space.Compact className="w-full">
          <Form.Item
            name={path}
            className="w-full"
            rules={[
              { required: false, message: "Please select or upload a document" }
            ]}
          >
            <Select
              allowClear
              className="flex-1"
              placeholder="Select an existing document"
              loading={loading}
              disabled={uploading || disabled || (loading && hasInitialValue)}
              onOpenChange={handleDropdownVisibleChange}
              options={files.map((file) => ({
                label:
                  file?.file_name ||
                  `File ${new Date(file.created).toLocaleDateString()}`,
                value: file.id
              }))}
              notFoundContent={loading ? <Spin size="small" /> : "No files"}
            />
          </Form.Item>
          <Upload
            multiple={false}
            beforeUpload={handleBeforeUpload}
            accept={ALLOWED_FILE_EXTENSIONS?.map((ext) => `.${ext}`).join(",")}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />} loading={uploading}>
              Upload
            </Button>
          </Upload>
        </Space.Compact>
      </Form.Item>

      <Form.Item>
        {loading && hasInitialValue ? (
          <Skeleton.Button active size="small" style={{ width: 120 }} />
        ) : (
          selectedFile && <Tag>{selectedFile?.file_name || "Document"}</Tag>
        )}
      </Form.Item>

      <Divider size={"small"} />

      <Flex vertical gap={"small"}>
        <Typography.Text type={"secondary"} className={"text-xs text-end"}>
          {ALLOWED_FILE_EXTENSIONS?.join(", ")?.toUpperCase()} • Up to{" "}
          {MAX_SIZE_MB}MB
        </Typography.Text>
      </Flex>
    </>
  )
}
